import { Fragment, useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle, memo } from 'react';
import { Button, Layout, Card, Space, Form, Input, Skeleton, Tooltip, Row, Col } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './index.less';
import Topic from './Topic';
import Facade from './Facade';
import Logic from './Logic';
import Setting from './Setting';
import Whitelists from './Setting/Whitelists';
import FinalCustom from './Setting/FinalCustom';
import { previewTemplateApi } from '@services/Wj/Template';
import { releaseSurveryApi } from '@services/Wj/Question';
import SurveyAutosaveContext from './SurveyAutosaveContext';

const SurveySaveStatus = memo(
    forwardRef(function SurveySaveStatus(_props, ref) {
        const [surveySavedVisible, setSurveySavedVisible] = useState(false);
        const [surveySavedClock, setSurveySavedClock] = useState('');

        useImperativeHandle(
            ref,
            () => ({
                notifySaved() {
                    const now = new Date();
                    const hh = String(now.getHours()).padStart(2, '0');
                    const mm = String(now.getMinutes()).padStart(2, '0');
                    setSurveySavedClock(`${hh}:${mm}`);
                    setSurveySavedVisible(true);
                },
            }),
            [],
        );

        return (
            <div className="wj-survey-save-status" aria-live="polite">
                <span className="wj-survey-save-hint">问卷内容修改后自动保存</span>
                {/* 始终占位，避免保存成功时插入第二行导致顶栏高度突变抖动 */}
                <span
                    className="wj-survey-save-done"
                    style={{
                        visibility: surveySavedVisible ? 'visible' : 'hidden',
                    }}
                    aria-hidden={!surveySavedVisible}
                >
                    <i className="bi bi-check-circle" aria-hidden="true" />
                    <span>最近保存 {surveySavedClock || '--:--'}</span>
                </span>
            </div>
        );
    }),
);

const Editsurvey = () => {
    useEffect(() => {
        document.title = '编辑问卷-医调研';
    }, []);

    const { Sider } = Layout;
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;
    const [topic, setTopic] = useState(true);
    const [logic, setLogic] = useState(false);
    const [setting, setSetting] = useState(false);
    const [facade, setFacade] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [anserArea, setAnserArea] = useState([]);
    const [preview, setPreview] = useState(undefined);
    const formRef = useRef(null);
    const saveStatusRef = useRef(null);
    const logicRef = useRef(null);
    const settingRef = useRef(null);
    const facadeRef = useRef(null);
    const whitelistsRef = useRef(null);
    const notifySurveySaved = useCallback(() => {
        saveStatusRef.current?.notifySaved();
    }, []);

    const surveyAutosaveContextValue = useMemo(() => ({ notifySaved: notifySurveySaved }), [notifySurveySaved]);

    useEffect(() => {
        switch (pathname) {
            case '/wj/surveys/new/create':
                setTopic(true);
                setLogic(false);
                setSetting(false);
                setFacade(false);
                break;
            case `/wj/surveys/edit/${location.state.id}`:
                setTopic(true);
                setLogic(false);
                setSetting(false);
                setFacade(false);
                break;

            case '/wj/surveys/new/logic':
                setTopic(false);
                setLogic(true);
                setSetting(false);
                setFacade(false);
                break;
            case '/wj/surveys/new/setting':
                setTopic(false);
                setLogic(false);
                setSetting(true);
                setFacade(false);
                break;
            case '/wj/surveys/new/whitelists':
                setTopic(false);
                setLogic(false);
                setSetting(true);
                setFacade(false);
                break;
            case '/wj/surveys/new/facade':
                setTopic(false);
                setLogic(false);
                setSetting(false);
                setFacade(true);
                break;
            default:
                break;
        }
    }, [pathname]);
    const onTopic = () => {
        if (location.state?.action === 'edit') {
            navigate(`/wj/surveys/edit/${location.state.id}`, { state: location.state });
        } else {
            navigate('/wj/surveys/new/create', { state: location.state });
        }
    };
    const onLogic = () => {
        navigate('/wj/surveys/new/logic', { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
    };
    const onSetting = () => {
        navigate('/wj/surveys/new/setting', { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
    };
    const onfacade = () => {
        navigate('/wj/surveys/new/facade', { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
    };
    const goBack = () => {
        if (location.pathname.includes('/wj/surveys/edit') && location.state.route !== 'newCreate') {
            navigate('/wj/surveys');
        } else if (location.state.route === 'newList' || location.state.route === 'newTemplate') {
            navigate('/wj/surveys');
        } else if (location.state.route === 'newCreate') {
            navigate('/wj/templates');
        }
    };
    const getQuestion = () => {
        releaseSurveryApi({ id: location.state.id, status: 1 });
        if (location.pathname.includes('new/whitelists')) {
            navigate(`/wj/surveys/${location.state.id}/share`, { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
        } else if (location.pathname.includes('new/setting')) {
            navigate(`/wj/surveys/${location.state.id}/share`, { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
        } else if (location.pathname.includes('new/facade')) {
            navigate(`/wj/surveys/${location.state.id}/share`, { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
        } else {
            navigate(`/wj/surveys/${location.state.id}/share`, { state: { ...location.state, title: formRef.current?.surveyTitle || location.state.title } });
        }
    };
    const onPreview = () => {
        navigate(`/wj/surveys/${location.state.id}/preview`, { state: { ...location.state, title: formRef.current?.surveyTitle } });
    };

    const saveTemplate = () => {
        navigate('/wj/templates');
    };

    useEffect(() => {
        if (location.state?.route === 'newCreate') {
            previewTemplateApi({ id: location.state.id }).then(res => {
                if (res.status_code === 200) {
                    console.log(res.data.name);
                    setTemplateName(res.data.name);
                }
            });
        }
    }, []);

    // useEffect(() => {
    //     setTimeout(() => {
    //         if (anserArea && anserArea.length) {
    //             console.log(anserArea);
    //             let a=document.querySelectorAll('.ql-container')
    //             console.log(a)
    //             if(a.length){
    //                 a.forEach(item=>{
    //                     // console.log(item);
    //                     item.addEventListener('mouseenter', (e) => {
    //                        item.style.border='1px dashed #1a73e8'
    //                        })
    //                 })
    //             }
    //         }
    //     }, 1000);

    // }, [anserArea]);

    const allDoc = e => {
        if (formRef.current) {
            formRef.current.allDoc(e);
        }
    };

    return (
        <SurveyAutosaveContext.Provider value={surveyAutosaveContextValue}>
            <div className="wj-editsurvey-root" onClick={e => allDoc(e)} onMouseDown={e => e.stopPropagation()}>
                {(location.pathname.includes('/wj/surveys/new') || location.pathname.includes('/wj/surveys/edit')) && (
                    <>
                        <div className="page_title_wrap wj-editsurvey-header">
                            <div className="page-left">
                                <div className="back" onClick={goBack}>
                                    <i className="bi bi-chevron-left"></i>
                                </div>
                                <span className="survey-title">{templateName}</span>
                            </div>

                            {location.state?.route === 'newCreate' ? (
                                <div className="page-center"></div>
                            ) : (
                                <div className="page-center">
                                    <span style={{ color: '#1890ff' }}>编辑</span>
                                    <i className="bi bi-arrow-right"></i>
                                    <span>分享</span>
                                </div>
                            )}

                            <div className="page-right">
                                <SurveySaveStatus ref={saveStatusRef} />
                                <div className="wj-survey-header-actions">
                                    <Button type="primary" onClick={onPreview} ghost>
                                        预览
                                    </Button>
                                    {location.state?.route === 'newCreate' ? (
                                        <Button type="primary" onClick={saveTemplate}>
                                            保存
                                        </Button>
                                    ) : (
                                        <Button type="primary" onClick={getQuestion}>
                                            发布
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <Layout className="create-box">
                            <Sider className="survey-action" theme="light" width={70}>
                                <div className="action-area">
                                    <div className="action-item" onClick={onTopic} style={topic ? { color: '#1a73e8' } : {}}>
                                        <i className="bi bi-grid"></i>
                                        <p>题型</p>
                                    </div>
                                    {location.state.route !== 'newCreate' && (
                                        <div className="action-item" onClick={onLogic} style={logic ? { color: '#1a73e8' } : {}}>
                                            <i className="bi bi-diagram-3"></i>
                                            <p>逻辑</p>
                                        </div>
                                    )}
                                    <div className="action-item" onClick={onSetting} style={setting ? { color: '#1a73e8' } : {}}>
                                        <i className="bi bi-gear"></i>
                                        <p>设置</p>
                                    </div>
                                    <div className="action-item" onClick={onfacade} style={facade ? { color: '#1a73e8' } : {}}>
                                        <i className="bi bi-palette"></i>
                                        <p>外观</p>
                                    </div>
                                </div>
                            </Sider>

                            {location.pathname.includes('new/create') && <Topic anserArea={anserArea} setAnserArea={setAnserArea} ref={formRef} preview={preview} />}
                            {location.pathname.includes('edit') && <Topic anserArea={anserArea} setAnserArea={setAnserArea} ref={formRef} preview={preview} />}
                            {location.pathname.includes('new/logic') && <Logic />}
                            {location.pathname.includes('new/whitelists') && <Whitelists />}
                            {location.pathname.includes('new/FinalCustom') && <FinalCustom />}
                            {location.pathname.includes('new/setting') && <Setting />}
                            {location.pathname.includes('new/facade') && <Facade />}
                        </Layout>
                    </>
                )}
            </div>
        </SurveyAutosaveContext.Provider>
    );
};

export default Editsurvey;
